<template>
  <div class="min-h-screen" :key="pageKey">
    <Header />
    
    <!-- Article Header -->
    <article class="pt-24">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <!-- 文章元信息 -->
        <div class="mb-8">
          <div class="flex items-center justify-between mb-4">
            <span class="px-4 py-2 bg-primary-10 text-primary rounded-full text-sm font-medium">
              {{ article.category }}
            </span>
            <span class="text-gray-800 dark:text-dark-muted text-sm">{{ article.date }}</span>
          </div>
          
          <h1 class="text-4xl md:text-5xl font-bold mb-6 gradient-text">{{ article.title }}</h1>
          <p class="text-xl text-gray-800 dark:text-dark-muted mb-8">{{ articleExcerpt }}</p>
          
          <!-- 作者信息 -->
          <div class="flex items-center mb-8">
            <div class="w-12 h-12 rounded-full icon-shell icon-shell-strong flex items-center justify-center mr-4">
              <FeatureIcon name="user" size="lg" variant="minimal" />
            </div>
            <div>
              <div class="font-semibold text-gray-800 dark:text-dark-text">{{ article.author }}</div>
              <div class="text-gray-800 dark:text-dark-muted text-sm">{{ article.position }}</div>
            </div>
          </div>
        </div>

        <!-- 文章特色图片 -->
        <div class="mb-12 rounded-2xl overflow-hidden">
          <NewsIllustration :type="article.illustrationType || 'default'" :variant="article.illustrationKey" size="lg" />
          <div class="text-center text-gray-600 dark:text-dark-muted text-sm mt-4">{{ article.imageCaption }}</div>
        </div>

        <!-- 文章内容 -->
        <div class="prose prose-lg max-w-none">
          <div v-html="article.content" class="article-content text-gray-800 dark:text-dark-text leading-relaxed"></div>
        </div>

        <!-- 相关标签 -->
        <div class="mt-12 pt-8 border-t border-dark-border">
          <h3 class="text-lg font-semibold mb-4 text-dark-text">相关标签</h3>
          <div class="flex flex-wrap gap-2">
            <span v-for="tag in article.tags" :key="tag"
                  class="px-4 py-2 bg-primary-10 border border-primary-20 rounded-full text-sm text-primary">
              {{ tag }}
            </span>
          </div>
        </div>

      </div>
    </article>

    <!-- 相关文章 -->
    <section class="py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="section-title gradient-text mb-12">相关文章</h2>
        
        <div v-if="relatedArticles && relatedArticles.length > 0" class="grid md:grid-cols-3 gap-8">
          <div v-for="related in relatedArticles" :key="related.id"
               class="card card-hover flex flex-col">
            <div class="relative mb-6 overflow-hidden rounded-xl">
              <NewsIllustration :type="related.illustrationType || 'default'" :variant="related.illustrationKey" size="md" />
            </div>

            <div class="mb-3">
              <span class="text-primary text-sm">{{ related.date }}</span>
            </div>

            <h3 class="text-xl font-bold mb-3 text-dark-text">{{ related.title }}</h3>
            <p class="text-gray-800 dark:text-dark-muted text-sm mb-4 line-clamp-2 flex-grow">{{ related.excerpt }}</p>

            <NuxtLink :to="`/news/${related.id}`" class="inline-flex items-center text-primary hover:text-primary-700 transition-colors text-sm mt-auto">
              阅读更多
              <svg class="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </NuxtLink>
          </div>
        </div>
        <div v-else class="text-center text-gray-500 dark:text-dark-muted py-8">
          暂无相关文章
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="py-32 relative">
      <div class="absolute inset-0 bg-gradient-to-r from-primary-5 via-accent-5 to-primary-5"></div>
      
      <div class="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 class="text-3xl font-bold mb-6 gradient-text">希望了解更多行业洞察？</h2>
        <div class="flex flex-col sm:flex-row gap-6 justify-center">
          <NuxtLink to="/news" class="btn-primary">
            浏览更多文章
          </NuxtLink>
          <button @click="contactUs" class="btn-secondary">
            联系我们
          </button>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script setup>
import { newsArticles, newsById } from '~/content/news'

const route = useRoute()
const routeId = computed(() => {
  const raw = route.params.id
  return Array.isArray(raw) ? raw[0] : raw
})

// 添加 key 属性用于强制刷新
const pageKey = computed(() => `news-${routeId.value || '1'}`)

const article = computed(() => {
  if (!routeId.value) return newsArticles[0]
  return newsById[routeId.value] || newsArticles[0]
})

const articleExcerpt = computed(() => article.value?.summary || '')

// 相关文章推荐
const relatedArticles = computed(() => {
  const currentId = routeId.value || '1'
  return newsArticles
    .filter((a) => a.id !== currentId)
    .slice(0, 3)
})

const contactUs = () => {
  window.location.href = '/about#contact'
}

// 设置页面meta
useHead({
  title: computed(() => `${article.value?.title || '文章详情'} - MAGIC资讯`),
  meta: computed(() => [
    { name: 'description', content: articleExcerpt.value || '文章详情内容' }
  ])
})

// 监听路由变化，确保页面内容正确更新
watch(() => route.params.id, (newId, oldId) => {
  if (newId !== oldId && newId) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}, { immediate: false })
</script>

<style scoped>
.article-content :deep(h2) {
  @apply text-2xl font-bold mt-8 mb-4 text-gray-800 dark:text-dark-text;
}

.article-content :deep(h3) {
  @apply text-xl font-bold mt-6 mb-3 text-gray-800 dark:text-dark-text;
}

.article-content :deep(h4) {
  @apply text-lg font-semibold mt-4 mb-2 text-gray-800 dark:text-dark-text;
}

.article-content :deep(p) {
  @apply mb-4 text-gray-700 dark:text-dark-muted leading-relaxed;
}

.article-content :deep(strong) {
  @apply font-semibold text-gray-800 dark:text-dark-text;
}
</style>
